import { stText } from "../../i18n/streamingPagesPresentation";
/**
 * ActivityIndicator -- Live activity banner for the Control Center header area.
 *
 * Subscribes to `activity.update` events from the media server event relay and
 * surfaces in-progress background tasks (library scans, metadata refreshes,
 * intro detection, etc.) with title + progress percentage.
 *
 * Auto-hides when the last activity reports complete: true.
 * Multiple concurrent activities are queued; the most-recently-updated is shown.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "../../utils/cn";
import { Icon } from "../ui/Icon";
import { useMediaServerEventStream } from "../../hooks/useMediaServerEventStream.js";
import type { ActivityUpdateEvent } from "../../types/streamingEvents.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TrackedActivity {
  activityId: string;
  title: string;
  progress: number | undefined;
  complete: boolean;
  serverType: string;
  lastSeen: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** How long (ms) to keep a completed activity visible before hiding it */
const COMPLETE_LINGER_MS = 2500;

/** Max age (ms) for a stale activity that never completed */
const STALE_THRESHOLD_MS = 60_000;

// ---------------------------------------------------------------------------
// ActivityIndicator
// ---------------------------------------------------------------------------

export const ActivityIndicator = () => {
  const [activities, setActivities] = useState<Map<string, TrackedActivity>>(new Map());
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleActivity = useCallback((event: ActivityUpdateEvent) => {
    setActivities((prev) => {
      const next = new Map(prev);
      const key = `${event.serverType}:${event.activityId}`;

      if (event.complete) {
        // Mark complete — will linger briefly then be removed
        const existing = next.get(key);
        if (existing) {
          next.set(key, { ...existing, complete: true, lastSeen: Date.now() });
        }
      } else {
        next.set(key, {
          activityId: event.activityId,
          title: event.title,
          progress: event.progress,
          complete: false,
          serverType: event.serverType,
          lastSeen: Date.now(),
        });
      }

      return next;
    });
  }, []);

  useMediaServerEventStream("activity.update", handleActivity);

  // Prune completed / stale activities after linger period
  useEffect(() => {
    if (activities.size === 0) return;

    const hasCompleted = [...activities.values()].some((a) => a.complete);
    const hasStale = [...activities.values()].some(
      (a) => Date.now() - a.lastSeen > STALE_THRESHOLD_MS
    );

    if (!hasCompleted && !hasStale) return;

    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      const now = Date.now();
      setActivities((prev) => {
        const next = new Map(prev);
        for (const [key, activity] of next) {
          if (activity.complete || now - activity.lastSeen > STALE_THRESHOLD_MS) {
            next.delete(key);
          }
        }
        return next;
      });
    }, COMPLETE_LINGER_MS);

    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [activities]);

  // Determine the activity to show: most recently updated incomplete, or most recent completed
  const activeActivities = [...activities.values()].filter((a) => !a.complete);
  const completedActivities = [...activities.values()].filter((a) => a.complete);
  const visible =
    activeActivities.sort((a, b) => b.lastSeen - a.lastSeen)[0] ??
    completedActivities.sort((a, b) => b.lastSeen - a.lastSeen)[0] ??
    null;

  if (!visible) return null;

  const isComplete = visible.complete;
  const progressPct =
    visible.progress !== undefined && visible.progress >= 0 && visible.progress <= 100
      ? visible.progress
      : null;

  return (
    <div
      className={cn(
        "relative flex items-center gap-3 overflow-hidden rounded-lg border px-3.5 py-2 text-sm transition-all duration-300",
        isComplete
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
          : "border-primary/20 bg-primary/5 text-foreground"
      )}
      role="status"
      aria-live="polite"
      aria-label={isComplete ? stText("completedTitle", { title: visible.title }) : stText("inProgressTitle", { title: visible.title })}
    >
      {/* Icon */}
      <span className="shrink-0">
        {isComplete ? (
          <Icon icon="lucide:check-circle" className="h-4 w-4 text-emerald-500" aria-hidden />
        ) : (
          <Icon
            icon="lucide:loader-circle"
            className="h-4 w-4 animate-spin text-primary-text"
            aria-hidden
          />
        )}
      </span>

      {/* Title + progress */}
      <span className="min-w-0 flex-1 truncate">
        <span className="font-medium">{visible.title}</span>
        {progressPct !== null && !isComplete && (
          <span className="ml-2 text-muted-foreground text-xs tabular-nums">
            {Math.round(progressPct)}%
          </span>
        )}
      </span>

      {/* Server badge */}
      <span className="shrink-0 text-[11px] text-muted-foreground capitalize">
        {visible.serverType}
      </span>

      {/* Progress bar (inline, only while in progress) */}
      {progressPct !== null && !isComplete && (
        <div className="absolute bottom-0 left-0 h-[2px] w-full overflow-hidden rounded-b-lg bg-primary/10">
          <div
            className="h-full bg-primary/50 transition-all duration-500"
            style={{ width: `${progressPct}%` }}
            aria-hidden
          />
        </div>
      )}
    </div>
  );
};
